import React, { useCallback, useContext, useEffect, useState } from 'react';
import { GoogleLoginResponse } from 'react-google-login';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Box, BoxProps, Divider, Flex, InputProps } from '@opendoor/bricks/core';
import 'url-polyfill';
import 'url-search-params-polyfill';

import { NamelessAccountContext } from '../../growth/auth/NamelessAccountContext';
import AccountEmailForm from './AccountEmailForm';
import AccountFormSubHeader from './AccountFormSubHeader';
import AccountPasswordForm from './AccountPasswordForm';
import CheckEmailForm from './CheckEmailForm';
import { GoogleAuth } from './GoogleAuth';
import PasswordlessResultPage from './PasswordlessResultPage';
import {
  AccountFormHeader,
  AccountFormWrapper,
  DisclaimerProps,
  EmailDisclaimerText,
  FormWrapper,
} from './shared';

const GOOGLE_CLIENT_ID = '121750171886-1udhbfom8jjascfnsscaupb7n4p17csm.apps.googleusercontent.com';

interface ICommonFormFields {
  analyticsName: string;
  required: boolean;
  unboundedWidth: boolean;
  value?: string;
  fontSize?: string;
  onChange: InputProps['onChange'];
  delay: number;
  label: string;
}

export interface IFormInputs {
  EmailInput: React.FC<
    ICommonFormFields & {
      type: 'email';
      id: 'email-login-input';
      name: 'email';
      minLength: number;
      errorMessage: string;
      placeholder: string;
      width: string;
      customEmailValidityCheck?: (email: string) => Promise<{ suggestion: string; valid: boolean }>;
      autoComplete: string;
      mb?: number;
    }
  >;
  NameInput: React.FC<
    ICommonFormFields & {
      type: 'text';
      id: string;
      name: 'name';
      className: 'od-register-name';
      placeholder: string;
      errorMessage?: string;
      autoComplete: string;
      autoFocus?: boolean;
      mb?: number;
    }
  >;
  PhoneInput: React.FC<
    ICommonFormFields & {
      type: 'tel';
      id: 'phone';
      className: 'od-register-phone';
      name: 'phone';
      autoComplete: string;
    }
  >;
  PasswordInput?: React.FC<
    ICommonFormFields & {
      autoFocus: boolean;
      className: 'od-register-password';
      errorMessage?: string;
      error?: boolean;
      id: 'password';
      name: 'password';
      placeholder: string;
      type: 'password';
    }
  >;
}

export interface IAccountFormProps {
  showNovoStyle?: boolean;
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email?: string;
  name?: string;
  phone?: string;
  loginHeaderText?: string;
  registrationHeaderText?: string;
  loginSubHeaderText?: string;
  registrationSubHeaderText?: string;
  emailLoading?: boolean;
  passwordLoading?: boolean;
  error?: string | boolean;
  hasGeneratedPassword?: boolean;
  isNewHuman?: boolean;
  onLogin: (
    email: string,
    password: string,
    googleIDToken?: string,
    facebookIDToken?: string,
  ) => void;
  onRegister: (
    email: string,
    name: string,
    password: string,
    phone: string,
    googleIDToken?: string,
    facebookIDToken?: string,
  ) => void;
  onHumanStatusRequest?: (email: string, fullName?: string) => void;
  onHumanStatusRequestOnePageReg?: (email: string) => boolean;
  phoneNumberInputOptional?: boolean;
  phoneNumberInputHidden?: boolean;
  openForgotPasswordInNewTab?: boolean;
  showGoogleAuth?: boolean;
  // TO DO: Refactor so we don't need to do this
  overrideHasSubmittedEmailCheckForLogin?: boolean;
  emailMismatch?: boolean;
  customEmailValidityCheck?: (email: string) => Promise<{ suggestion: string; valid: boolean }>;
  customEmailValidityCheckDelay?: number;
  requestResetPassword?: (email: string) => void;
  containerProps?: BoxProps;
  hidePasswordField?: boolean; // When this is set to true, we hide the account password form
  onPasswordlessLoginRequest?: (
    email: string,
  ) => Promise<{ success: boolean; errors?: Array<string> }>;
  onboardingAccountPageCTA?: boolean;
  fullNamePlaceholder?: string;
  fullNameLabel?: string;
  disclaimerProps?: DisclaimerProps;
  passwordFormActionText?: string;
  NameInput?: IFormInputs['NameInput'];
  PhoneInput?: IFormInputs['PhoneInput'];
  EmailInput?: IFormInputs['EmailInput'];
  PasswordInput?: IFormInputs['PasswordInput'];
}

const AccountForm: React.FC<IAccountFormProps> = ({
  analyticsName,
  phone = '',
  email: initialEmail = '',
  name: initialName = '',
  loginHeaderText = 'Sign in or create an account',
  registrationHeaderText = 'Finish creating an account',
  loginSubHeaderText,
  registrationSubHeaderText,
  emailLoading,
  passwordLoading,
  error,
  phoneNumberInputOptional,
  phoneNumberInputHidden,
  openForgotPasswordInNewTab,
  showGoogleAuth = false,
  overrideHasSubmittedEmailCheckForLogin,
  emailMismatch,
  customEmailValidityCheck,
  customEmailValidityCheckDelay,
  requestResetPassword,
  containerProps,
  hidePasswordField = false,
  onPasswordlessLoginRequest,
  onboardingAccountPageCTA,
  fullNamePlaceholder,
  fullNameLabel,
  disclaimerProps,
  passwordFormActionText,
  EmailInput,
  NameInput,
  PhoneInput,
  PasswordInput,
  onHumanStatusRequest,
  onLogin,
  onRegister,
  ...props
}) => {
  const [email, setEmail] = useState<string>(initialEmail);

  const [name, setName] = useState<string>(initialName);

  //Login States

  const [isNewHuman, setIsNewHuman] = useState<boolean>(props.isNewHuman);

  const [hasGeneratedPassword, setHasGeneratedPassword] = useState<boolean>(
    props.hasGeneratedPassword,
  );

  const [googleLoginAttempt, setGoogleLoginAttempt] = useState<boolean>(false);

  const [isRequestingPasswordlessLogin, setIsRequestingPasswordlessLogin] =
    useState<boolean>(false);

  const [hasPasswordlessLoginRequestSucceeded, setHasPasswordlessLogingRequestSucceeded] =
    useState<boolean>();

  const { shouldRegisterOnEmailSubmit, emailRegistrationError } =
    useContext(NamelessAccountContext);

  const handleLogin = (password: string) => {
    setGoogleLoginAttempt(false);
    onLogin(email, password);
  };

  const handleGoogleLogin = async (googleLoginResponse: GoogleLoginResponse) => {
    setGoogleLoginAttempt(true);
    onRegister(null, null, null, null, googleLoginResponse.tokenId);
  };

  const handleRegister = (password: string, name: string, phone: string) => {
    setGoogleLoginAttempt(false);
    onRegister(email, name, password, phone);
  };

  const handleEmailSubmit = useCallback(
    async (email: string, name?: string) => {
      if (onHumanStatusRequest) {
        await onHumanStatusRequest(email, name);
      }
      setEmail(email);
      setName(name);
    },
    [onHumanStatusRequest],
  );

  const handleRequestPasswordlessLogin = async () => {
    if (onPasswordlessLoginRequest) {
      setIsRequestingPasswordlessLogin(true);

      const { success } = await onPasswordlessLoginRequest(email);
      setIsRequestingPasswordlessLogin(false);
      setHasPasswordlessLogingRequestSucceeded(!!success);
    }
  };

  // Toggles to the email request view and allows user to change the email being used to log in
  const resetValidEmail = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    setEmail('');
    setHasGeneratedPassword(false);
    setIsNewHuman(false);
  };

  const socialLoginErrorMessage = (provider: string, emailMismatch: boolean, email: string) => {
    if (emailMismatch && email && email !== '') {
      return `You tried to sign in with an email that isn't associated with this home address. Please sign in again with ${email}.`;
    }

    return `${provider} authorization failed. Please try again or use the general login.`;
  };

  useEffect(() => {
    setEmail(initialEmail);
    /**
     * If an email was passed check its status so that we can know whether to show
     * the log in or sign up page
     */
    if (initialEmail) {
      handleEmailSubmit(initialEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); //Making sure to only call on onmount with the initial Email

  useEffect(() => {
    setIsNewHuman(!!props.isNewHuman);
    setHasGeneratedPassword(props.hasGeneratedPassword);
  }, [props.hasGeneratedPassword, props.isNewHuman]);

  useEffect(
    () => {
      if (props.hasGeneratedPassword !== hasGeneratedPassword) {
        setHasGeneratedPassword(!!props.hasGeneratedPassword);
      }
      if (props.isNewHuman !== isNewHuman) {
        setIsNewHuman(!!props.isNewHuman);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialEmail, props.hasGeneratedPassword, props.isNewHuman],
  );

  const hasEmailRegisterError = shouldRegisterOnEmailSubmit && !!emailRegistrationError;

  const hasSubmittedEmail = email && email.length > 0 && !emailLoading && !hasEmailRegisterError;

  if (hasSubmittedEmail && hasGeneratedPassword) {
    return (
      <CheckEmailForm
        analyticsName={analyticsName}
        email={email}
        hasGeneratedPassword={hasGeneratedPassword}
        onResetValidEmail={resetValidEmail}
        requestResetPassword={requestResetPassword}
      />
    );
  }

  if (hasPasswordlessLoginRequestSucceeded !== undefined) {
    return (
      <PasswordlessResultPage
        hasSucceeded={hasPasswordlessLoginRequestSucceeded}
        email={email}
        analyticsName={analyticsName}
      />
    );
  }

  // TODO: Clean up error handling
  const hasGoogleError = googleLoginAttempt && (emailMismatch || error);

  let loginErrorMessage = '';
  if (emailMismatch && email) {
    loginErrorMessage = `You are logged in with an email that isn't associated with this home address. Please sign in again with ${email}.`;
  } else if (error) {
    loginErrorMessage = 'Invalid email or password';
  }

  let headerText = loginHeaderText;

  if (hasSubmittedEmail && isNewHuman) {
    headerText = registrationHeaderText;
  } else if (hasSubmittedEmail) {
    headerText = 'Welcome back';
  }

  return (
    <AccountFormWrapper {...containerProps}>
      {/* only show disclaimer when user enters their email */}
      <AccountFormHeader mb={hasSubmittedEmail ? '7' : '5'}>{headerText}</AccountFormHeader>
      {!hasSubmittedEmail && <EmailDisclaimerText analyticsName={analyticsName} />}
      <AccountFormSubHeader
        isNewHuman={isNewHuman}
        email={email}
        hasSubmittedEmail={hasSubmittedEmail}
        loginSubHeaderText={loginSubHeaderText}
        registrationSubHeaderText={registrationSubHeaderText}
        onValidEmailReset={resetValidEmail}
        hidePasswordField={hidePasswordField}
        phoneNumberInputHidden={phoneNumberInputHidden}
      />

      {showGoogleAuth && (!hasSubmittedEmail || overrideHasSubmittedEmailCheckForLogin) && (
        <Flex justifyContent="center" alignItems="center" pt={4}>
          <GoogleAuth onAuthed={handleGoogleLogin} clientId={GOOGLE_CLIENT_ID} />
        </Flex>
      )}
      {hasGoogleError && (
        <ErrorPrompt message={socialLoginErrorMessage('Google', emailMismatch, email)} />
      )}
      {showGoogleAuth && (!hasSubmittedEmail || overrideHasSubmittedEmailCheckForLogin) && (
        <Flex gridGap="8px" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Box flex={1}>
            <Divider />
          </Box>
          <Box color="warmgrey700" padding={7}>
            or
          </Box>
          <Box flex={1}>
            <Divider />
          </Box>
        </Flex>
      )}
      <FormWrapper>
        {hasSubmittedEmail ? (
          <AccountPasswordForm
            analyticsName={analyticsName}
            email={email}
            name={name}
            phone={phone}
            loading={passwordLoading}
            isNewHuman={isNewHuman}
            loginError={googleLoginAttempt ? false : loginErrorMessage}
            onLogin={handleLogin}
            onRegister={handleRegister}
            phoneNumberInputOptional={phoneNumberInputOptional}
            phoneNumberInputHidden={phoneNumberInputHidden}
            openForgotPasswordInNewTab={openForgotPasswordInNewTab}
            showPasswordlessLogin={!isNewHuman && !!onPasswordlessLoginRequest}
            onRequestPasswordlessLogin={handleRequestPasswordlessLogin}
            isRequestingPasswordlessLogin={isRequestingPasswordlessLogin}
            hidePasswordField={hidePasswordField}
            onboardingAccountPageCTA={onboardingAccountPageCTA}
            fullNamePlaceholder={fullNamePlaceholder ? fullNamePlaceholder : 'Jasper Smith'}
            fullNameLabel={fullNameLabel ? fullNameLabel : 'Full name'}
            disclaimerProps={disclaimerProps}
            passwordFormActionText={passwordFormActionText}
            NameInput={NameInput}
            PhoneInput={PhoneInput}
            PasswordInput={PasswordInput}
          />
        ) : (
          <AccountEmailForm
            analyticsName={analyticsName}
            email={email}
            loading={emailLoading}
            onSubmit={handleEmailSubmit}
            openForgotPasswordInNewTab={openForgotPasswordInNewTab}
            customEmailValidityCheck={customEmailValidityCheck}
            customEmailValidityCheckDelay={customEmailValidityCheckDelay}
            showForgotPassword={!hidePasswordField}
            EmailInput={EmailInput}
          />
        )}
      </FormWrapper>
    </AccountFormWrapper>
  );
};

export default AccountForm;
